import React from 'react';
import { Modal, Typography } from 'antd';
import { THEME_COLORS } from '../../utils/colors';

const { Title, Text, Paragraph } = Typography;

const PrivacyPolicyModal = ({ open, onCancel }) => {
  return (
    <Modal
      title={
        <div style={{ textAlign: "center" }}>
          <Title
            level={3}
            style={{
              marginBottom: 4,
              fontWeight: 700,
              color: THEME_COLORS.DARK.text.primary,
              fontSize: '1.5rem'
            }}
          >
            Hooptie.io — Privacy Policy
          </Title>
          <Text
            style={{
              color: THEME_COLORS.DARK.text.tertiary,
              fontSize: 12,
              lineHeight: 1.6
            }}
          >
            Last Updated: December 9, 2025
          </Text>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={900}
      style={{ top: 20 }}
      closeIcon={<span style={{ color: THEME_COLORS.DARK.text.primary, fontSize: '20px' }}>×</span>}
      bodyStyle={{
        maxHeight: 'calc(100vh - 200px)',
        overflowY: 'auto',
        padding: '24px',
        background: THEME_COLORS.DARK.bg.secondary,
        color: THEME_COLORS.DARK.text.secondary,
        lineHeight: 1.8
      }}
    >
      <div style={{ color: THEME_COLORS.DARK.text.secondary, lineHeight: 1.8 }}>
        <Paragraph style={{ fontSize: 15, marginBottom: 24, color: THEME_COLORS.DARK.text.secondary }}>
          Hooptie ("Hooptie.io," "we," "our," or "us") provides automotive market insights, analytics tools, and AI-powered recommendations for independent dealerships. This Privacy Policy explains how we collect, use, protect, and share your information when you use our website, platform, and services (the "Services").
        </Paragraph>

        <Paragraph style={{ fontSize: 15, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          By accessing or using Hooptie.io, you agree to the terms of this Privacy Policy.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          1. Information We Collect
        </Title>

        <Title level={5} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 24, marginBottom: 12, fontSize: '1.1rem' }}>
          1.1 Information You Provide
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          We may collect the following information when you sign up or use the platform:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>
            <strong style={{ color: THEME_COLORS.DARK.text.primary }}>Account information:</strong> Name, email address, dealership name, phone number, and password.
          </li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>
            <strong style={{ color: THEME_COLORS.DARK.text.primary }}>Billing information:</strong> Payment details processed securely by Stripe (we do not store full card numbers on our servers).
          </li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>
            <strong style={{ color: THEME_COLORS.DARK.text.primary }}>Dealership data:</strong> Inventory information you upload or connect manually, including VINs, prices, mileage, and notes.
          </li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>
            <strong style={{ color: THEME_COLORS.DARK.text.primary }}>Communications:</strong> Messages you send to us, support requests, and feedback.
          </li>
        </ul>

        <Title level={5} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 24, marginBottom: 12, fontSize: '1.1rem' }}>
          1.2 Automatically Collected Information
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          When you use Hooptie.io, we automatically collect:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>
            <strong style={{ color: THEME_COLORS.DARK.text.primary }}>Usage data:</strong> Pages visited, buttons clicked, time spent on different parts of the platform.
          </li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>
            <strong style={{ color: THEME_COLORS.DARK.text.primary }}>Device & technical data:</strong> Browser type, IP address, operating system, referral URLs.
          </li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>
            <strong style={{ color: THEME_COLORS.DARK.text.primary }}>Analytics data:</strong> Session replays, heatmaps, and usage metrics via tools like PostHog or similar analytics providers.
          </li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>
            <strong style={{ color: THEME_COLORS.DARK.text.primary }}>Cookies:</strong> Used for authentication, personalization, and improving platform performance.
          </li>
        </ul>

        <Title level={5} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 24, marginBottom: 12, fontSize: '1.1rem' }}>
          1.3 Third-Party Data
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 24, color: THEME_COLORS.DARK.text.secondary }}>
          We may receive data from third-party APIs, such as:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 32, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Market listings and sold vehicle data</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Vehicle history providers</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>VIN decoding services</li>
        </ul>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          This information helps power our insights, pricing models, and AI recommendations.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          2. How We Use Your Information
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          We use the information we collect to:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Provide, maintain, and improve the Hooptie platform</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Generate AI-powered insights and recommendations</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Analyze dealership market conditions and inventory performance</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Customize your user experience</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Communicate updates, product announcements, and support responses</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Process payments and manage billing</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Prevent fraud, misuse, or unauthorized access</li>
        </ul>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, fontWeight: 500, color: THEME_COLORS.DARK.text.primary }}>
          We do not sell your personal information to third parties.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          3. How We Share Information
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 24, color: THEME_COLORS.DARK.text.secondary }}>
          We may share your information in the following situations:
        </Paragraph>

        <Title level={5} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 24, marginBottom: 12, fontSize: '1.1rem' }}>
          3.1 Service Providers
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          We share data with trusted vendors who help operate the platform, including:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Stripe (billing and payments)</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Analytics providers (PostHog, etc.)</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Hosting providers (Heroku, AWS, or similar)</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>AI processing services (OpenAI or similar)</li>
        </ul>
        <Paragraph style={{ fontSize: 14, marginBottom: 24, color: THEME_COLORS.DARK.text.secondary }}>
          These companies only access information as needed to perform their functions.
        </Paragraph>

        <Title level={5} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 24, marginBottom: 12, fontSize: '1.1rem' }}>
          3.2 Legal Requirements
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 24, color: THEME_COLORS.DARK.text.secondary }}>
          We may disclose information if required by law, government request, or to protect our rights.
        </Paragraph>

        <Title level={5} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 24, marginBottom: 12, fontSize: '1.1rem' }}>
          3.3 Business Transfers
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          If Hooptie is involved in a merger, acquisition, or asset sale, user information may be transferred as part of the transaction.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          4. Data Security
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          We implement reasonable security measures—including encryption, access controls, and regular monitoring—to protect your information.
        </Paragraph>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, fontStyle: 'italic', color: THEME_COLORS.DARK.text.tertiary }}>
          However, no online service can guarantee absolute security.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          5. Your Choices & Rights
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          Depending on your location, you may have rights to:
        </Paragraph>
        <ul style={{ fontSize: 14, marginBottom: 24, paddingLeft: 24, color: THEME_COLORS.DARK.text.secondary }}>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Access and download your data</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Update or correct your information</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Request deletion of your account</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Opt out of marketing communications</li>
          <li style={{ marginBottom: 8, color: THEME_COLORS.DARK.text.secondary }}>Restrict certain processing</li>
        </ul>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          You can manage most settings in your account or by contacting us at{' '}
          <a href="mailto:support@hooptie.io" style={{ color: THEME_COLORS.DARK.text.primary, textDecoration: 'underline' }}>
            support@hooptie.io
          </a>
          .
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          6. Data Retention
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          We retain your information as long as your account is active or needed to provide the Services.
        </Paragraph>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          If you request deletion, we will remove your data unless required to keep it for legal or billing purposes.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          7. Children's Privacy
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          Hooptie is not intended for individuals under 18, and we do not knowingly collect information from minors.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          8. International Users
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          If you access Hooptie from outside the United States, your data may be transferred to and processed in the U.S., where privacy laws may differ.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          9. Changes to This Privacy Policy
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 32, color: THEME_COLORS.DARK.text.secondary }}>
          We may update this Privacy Policy from time to time. If changes are significant, we will notify you via email or platform notice.
        </Paragraph>

        <Title level={4} style={{ color: THEME_COLORS.DARK.text.primary, marginTop: 32, marginBottom: 16, fontSize: '1.25rem' }}>
          10. Contact Us
        </Title>
        <Paragraph style={{ fontSize: 14, marginBottom: 16, color: THEME_COLORS.DARK.text.secondary }}>
          If you have questions about this Privacy Policy or how your data is handled, please contact us:
        </Paragraph>
        <div style={{ fontSize: 14, marginBottom: 32, paddingLeft: 8 }}>
          <Paragraph style={{ marginBottom: 4, fontWeight: 500, color: THEME_COLORS.DARK.text.primary }}>Hooptie, LLC</Paragraph>
          <Paragraph style={{ marginBottom: 4, color: THEME_COLORS.DARK.text.secondary }}>
            Email:{' '}
            <a href="mailto:support@hooptie.io" style={{ color: THEME_COLORS.DARK.text.primary, textDecoration: 'underline' }}>
              support@hooptie.io
            </a>
          </Paragraph>
          <Paragraph style={{ marginBottom: 4, color: THEME_COLORS.DARK.text.secondary }}>
            Website:{' '}
            <a href="https://hooptie.io" target="_blank" rel="noopener noreferrer" style={{ color: THEME_COLORS.DARK.text.primary, textDecoration: 'underline' }}>
              https://hooptie.io
            </a>
          </Paragraph>
        </div>
      </div>
    </Modal>
  );
};

export default PrivacyPolicyModal;

